<script setup lang="ts">
import { ref, computed } from "vue";
import NotificationList from "@/components/models/message/notification/NotificationList.vue";
import { useNotification } from "@/composables/models/notification/useNotification";
import SecondaryButton from "@/components/shared/buttons/SecondaryButton.vue";
import { definePage } from "unplugin-vue-router/runtime";
import { useI18n } from "vue-i18n";
import { ENotificationTypes, type TNotification } from "@/api/models/message/notification";

definePage({
  meta: {
    title: "generic.entity.notification",
    checkPrivileges: () => {
      return true;
    },
  },
});

const { t } = useI18n();

type TFilter = ENotificationTypes | "all";

const currentFilter = ref<TFilter>("all");

const {
  data: notifications,
  isPending: isLoading,
  resetUnreadNotificationCount,
} = useNotification();

const filterNotifications = (filter: TFilter) => {
  currentFilter.value = filter;
};

const filteredNotifications = computed(() => {
  if (!notifications.value) return [];
  if (currentFilter.value === "all") return notifications.value;

  return notifications.value.filter(
    (notification: TNotification) => notification.notification_type === currentFilter.value
  );
});

const countByType = (type: ENotificationTypes) =>
  notifications.value?.filter(({ notification_type }) => notification_type === type).length ?? 0;

const notificationCounts = computed(() => ({
  all: notifications.value?.length ?? 0,
  treatment_plan: countByType(ENotificationTypes.TREATMENT_PLAN),
  patient_message: countByType(ENotificationTypes.PATIENT_MESSAGE),
}));

const filters = computed(() => [
  {
    value: "all" as const,
    label: t("models.notification.filters.all"),
    count: notificationCounts.value.all,
  },
  {
    value: ENotificationTypes.TREATMENT_PLAN,
    label: t("models.notification.filters.treatmentPlan"),
    count: notificationCounts.value.treatment_plan,
  },
  {
    value: ENotificationTypes.PATIENT_MESSAGE,
    label: t("models.notification.filters.message"),
    count: notificationCounts.value.patient_message,
  },
]);
</script>

<template>
  <div class="tw:w-full tw:items-center tw:flex tw:flex-col tw:relative tw:!px-4 tw:sm:!px-8">
    <div class="tw:max-w-5xl tw:w-full">
      <div
        class="tw:sticky tw:!pt-6 tw:sm:!pt-10 tw:top-[var(--v-layout-top)] tw:bg-background tw:w-full tw:z-layer-5"
        data-testid="notification-center-header"
      >
        <div class="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:!mb-6 tw:sm:!mb-8">
          <div
            class="tw:min-w-0 tw:truncate tw:text-on-surface tw:text-2xl tw:md:text-3xl tw:font-bold"
          >
            {{ t("models.notification.title") }}
          </div>
          <SecondaryButton
            class="orbis-btn--quiet orbis-btn--primary-ink tw:!shrink-0"
            size="default"
            :aria-label="t('models.notification.markAllAsRead')"
            @click="resetUnreadNotificationCount"
          >
            <v-icon
              icon="mdi-check-all"
              size="16"
            />
            <span class="tw:hidden tw:sm:inline">
              {{ t("models.notification.markAllAsRead") }}
            </span>
          </SecondaryButton>
        </div>
        <div
          class="tw:flex tw:overflow-x-auto no-scrollbar tw:border-b tw:border-on-surface/[0.06]"
        >
          <div
            v-for="filter in filters"
            :key="filter.value"
            class="tw:relative tw:flex tw:shrink-0 tw:items-center tw:gap-2 tw:!px-3 tw:!py-3 tw:!cursor-pointer tw:transition-colors tw:font-[640]"
            :class="
              currentFilter === filter.value
                ? 'tw:text-on-surface'
                : 'tw:text-on-surface/55 tw:hover:text-on-surface/75'
            "
            @click="filterNotifications(filter.value)"
          >
            <span class="tw:text-sm tw:whitespace-nowrap">{{ filter.label }}</span>
            <span
              class="tw:text-xs tw:font-semibold tw:rounded-lg tw:!px-2 tw:!py-0.5 tw:shrink-0"
              :class="
                currentFilter === filter.value
                  ? 'tw:bg-secondary/10 tw:text-secondary'
                  : 'tw:bg-on-surface/5 tw:text-on-surface/50'
              "
            >
              {{ filter.count }}
            </span>
            <span
              v-if="currentFilter === filter.value"
              class="tw:absolute tw:inset-x-2 tw:-bottom-px tw:h-[3px] tw:rounded-full tw:bg-secondary"
            />
          </div>
        </div>
      </div>

      <NotificationList
        :notifications="filteredNotifications"
        :is-loading="isLoading"
      />
    </div>
  </div>
</template>
